/*
 * Filtros para la lista de productos
 *
 * Contiene los componentes solicitados para filtrar la lista de los registros
 * productos
 *
 * Created on Thu Jun 15 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>
    <!-- #region begin::Searcher and options for filtering -->
    <b-form-row>

      <!-- #region begin::Searcher and filters -->
      <b-col md="8">
        <b-form-row align-h="start">
          <b-col md="4">
            <BasicSearcher
              ref="basic-searcher"
              :callback="handleSearch"
              placeholder="Buscar productos"
            />
          </b-col>

          <!-- #region begin::Brand filter -->
          <b-col md="2">
            <v-select
              v-model="selectedBrand"
              label="Name"
              class="style-chooser"
              placeholder="Marca"
              :options="getBrands"
              :disabled="isLoadingProducts"
            />
          </b-col>
          <!-- #endregion begin::Brand filter -->

          <!-- #region begin::Category filter -->
          <b-col md="2">
            <v-select
              v-model="selectedCategory"
              label="Name"
              class="style-chooser"
              placeholder="Subcategoría"
              :options="getCategories"
              :disabled="isLoadingProducts"
            />
          </b-col>
          <!-- #endregion begin::Category filter -->

          <!-- #region begin::Type filter -->
          <b-col
            v-if="$ability.can('read', 'Productos_filtertype')"
            md="2"
          >
            <v-select
              v-model="selectedType"
              label="text"
              class="style-chooser"
              placeholder="Tipo"
              :options="getTypes"
              :disabled="isLoadingProducts"
            />
          </b-col>
          <!-- #endregion begin::Type filter -->

          <!-- #region begin::Location filter -->
          <b-col md="2">
            <v-select
              v-if="showLocationFilter"
              v-model="selectedLocation"
              label="text"
              class="style-chooser"
              placeholder="Ubicación"
              :options="getLocations"
              :disabled="isLoadingProducts || (selectedOrigin !== '' && selectedOrigin !== null)"
            />
          </b-col>
          <!-- #endregion begin::Location filter -->

        </b-form-row>
      </b-col>
      <!-- #endregion begin::Searcher and filters -->

      <b-col
        v-if="showOptionsButtons && $ability.can('create', 'Product')"
        cols="12"
        md="auto"
        class="ml-auto"
      >
        <b-button
          variant="principal-btn"
          class="my-1 my-lg-0 w-100 principal-btn"
          :to="{ name: 'new-product' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">Nuevo producto</span>
        </b-button>
      </b-col>
      <!-- #endregion end::New product button -->

      <!-- #region begin::Sending CSV file button -->
      <b-col
        v-if="showOptionsButtons && $ability.can('download', 'Products')"
        cols="12"
        md="auto"
      >
        <b-button
          variant="flat-success w-100"
          class="my-1 my-lg-0 w-100 csv-btn"
          :disabled="isSendingEmail"
          @click="onSendProductsEmail"
        >
          <b-spinner
            v-if="isSendingEmail"
            small
          />
          <feather-icon
            v-if="!isSendingEmail"
            icon="MailIcon"
          />
        </b-button>
      </b-col>
      <!-- #endregion end::Sending CSV file button -->
    </b-form-row>
    <!-- #endregion end::Searcher and options for filtering -->

    <!-- #region begin::Switches -->
    <b-form-row v-if="$ability.can('read', 'Productos_filterPendingImage') || $ability.can('read', 'Productos_filterPriceToBeDefined')">
      <div
        v-if="$ability.can('read', 'Productos_filterPendingImage')"
        class="custom-control custom-control-inline custom-switch mt-1"
      >
        <b-card-text class="mb-0 ml-1">
          Imagen pendiente
        </b-card-text>
        <b-form-checkbox
          v-model="switchPendingImage"
          checked="false"
          class="custom-control-primary ml-1"
          name="check-button"
          switch
        />
      </div>
      <div
        v-if="$ability.can('read', 'Productos_filterPriceToBeDefined')"
        class="custom-control custom-control-inline custom-switch mt-1"
      >
        <b-card-text class="mb-0 ml-1">
          Precio por definir
        </b-card-text>
        <b-form-checkbox
          v-model="switchPriceToBeDefined"
          checked="false"
          class="custom-control-primary ml-1"
          name="check-button"
          switch
        />
      </div>
    </b-form-row>
    <!-- #endregion end::Switches -->
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import {
  BButton, BCol, BFormRow, BCardText, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getError from '@/helpers/ErrorsHandler'

export default {
  components: {
    BCol,
    BButton,
    vSelect,
    BSpinner,
    BFormRow,
    BCardText,
    BasicSearcher,
    BFormCheckbox,
  },
  props: {
    /**
     * Mostrar botones de opciones para crear producto y descargar CSV
     */
    showOptionsButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Mostrar filtro de ubicación
     */
    showLocationFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * Determina si se cargan los filtros globales del estado
     */
    canApplyGlobalFilters: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedType: null,
      selectedBrand: null,
      selectedCategory: null,
      selectedLocation: null,
      switchPendingImage: false,
      switchPriceToBeDefined: false,

      isSendingEmail: false,
    }
  },
  computed: {
    ...mapState('products', ['isLoadingProducts']),
    ...mapGetters({
      getBrands: 'brands/getBrands',
      getTypes: 'products/getTypes',
      getLocations: 'pieces/getLocations',
      getCategories: 'categories/getCategories',

      getLocationTerm: 'products/getLocationTerm',
      getSelectedOrigin: 'transfers/getSelectedOrigin',

      /**
       * Filtros globales
       */
      getFilteredBrand: 'filters/getFilteredBrand',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredSubcategory: 'filters/getFilteredSubcategory',
      getFilteredProductType: 'filters/getFilteredProductType',
      getFilteredPendingImage: 'filters/getFilteredPendingImage',
      getFilteredPiecesLocation: 'filters/getFilteredPiecesLocation',
      getFilteredPriceToBeDefined: 'filters/getFilteredPriceToBeDefined',
    }),
    /**
     * Término de ubicación de búsqueda del store de productos
     */
    locationTerm: {
      get() { return this.getLocationTerm },
    },
    /**
     * Ubicación de origen de orden de traspaso
     */
    selectedOrigin: {
      get() { return this.getSelectedOrigin },
    },
    /**
     * Marca filtrada (filtro en store)
     */
    filteredBrand: {
      get() { return this.getFilteredBrand },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
    },
    /**
     * Subcategoría filtrada (filtro en store)
     */
    filteredSubcategory: {
      get() { return this.getFilteredSubcategory },
    },
    /**
     * Tipo de producto filtrado (filtro en store)
     */
    filteredProductType: {
      get() { return this.getFilteredProductType },
    },
    /**
     * Ubicación de piezas filtrada (filtro en store)
     */
    filteredPiecesLocation: {
      get() { return this.getFilteredPiecesLocation },
    },

    /**
     * Switch de imagen pendiente (filtro en store)
     */
    filteredPendingImage: {
      get() { return this.getFilteredPendingImage },
    },

    /**
     * Switch de precio por definir (filtro en store)
     */
    filteredPriceToBeDefined: {
      get() { return this.getFilteredPriceToBeDefined },
    },
  },

  /**
  * Carga información del estado
  *
  * @summary Si existe información relacionada con los filtros cargados en el store,
  * se cargan en el componente
  */
  mounted() {
    // If we can apply global filters, load them from the store into the filter component
    if (this.canApplyGlobalFilters) {
      // Carga el término de búsqueda (filtro en store)
      if (this.filteredSearchTerm) {
        this.$refs['basic-searcher'].loadSearchTerm(this.filteredSearchTerm)
      }

      // Carga la marca filtrada (filtro en store)
      this.selectedBrand = this.filteredBrand ? { Name: this.filteredBrand } : null

      // Carga la subcategoría filtrada (filtro en store)
      this.selectedCategory = this.filteredSubcategory ? { Name: this.filteredSubcategory } : null

      // Carga el tipo de producto filtrado (filtro en store)
      this.selectedType = this.filteredProductType ? { text: this.filteredProductType } : null

      // Carga la ubicación filtrada (filtro en store)
      this.selectedLocation = this.filteredPiecesLocation ? { text: this.filteredPiecesLocation } : null

      // Carga los switches de imagen pendiente y precio por definir (filtro en store)
      this.switchPendingImage = this.filteredPendingImage || false
      this.switchPriceToBeDefined = this.filteredPriceToBeDefined || false
    } else {
      this.selectedLocation = this.locationTerm ? { text: this.locationTerm } : null
    }

    this.$watch('selectedBrand', brand => {
      this.$emit('on-filter', { type: 'brand', value: brand !== null ? brand.Name : '' })
    })

    this.$watch('selectedCategory', category => {
      this.$emit('on-filter', { type: 'category', value: category !== null ? category.Name : '' })
    })

    this.$watch('selectedType', type => {
      this.$emit('on-filter', { type: 'type', value: type !== null ? type.text : '' })
    })

    this.$watch('selectedLocation', location => {
      this.$emit('on-filter', { type: 'location', value: location !== null ? location.text : '' })
    })

    this.$watch('switchPendingImage', value => {
      this.$emit('on-filter', { type: 'pendingImage', value })
    })

    this.$watch('switchPriceToBeDefined', value => {
      console.log('switchPriceToBeDefined', value)
      this.$emit('on-filter', { type: 'priceToBeDefined', value })
    })
  },
  methods: {
    ...mapActions({
      sendProductsEmail: 'products/sendProductsEmail',
    }),
    /**
    * Evento del buscador básico
    * @summary Emite un evento con el término de búsqueda y limpia los campos de selección
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearch(searchTerm) {
      this.$emit('on-filter', { type: 'search', value: searchTerm })

      this.selectedType = null
      this.selectedBrand = null
      this.selectedCategory = null
      this.selectedLocation = null
      this.switchPendingImage = false
      this.switchPriceToBeDefined = false
    },

    /**
    * Descarga de archivo CSV
    * @summary Descarga el archivo CSV de productos con los parámetros necesarios
    * tomados de los filtros
    */
    async onSendProductsEmail() {
      try {
        this.isSendingEmail = true
        await this.sendProductsEmail(`${process.env.VUE_APP_API_URL}/products/reporter?filter[search]=${this.filteredSearchTerm}&brand=${this.filteredBrand}&category=${this.filteredSubcategory}&tipo=${this.filteredProductType}&location=${this.filteredPiecesLocation}&pendingImage=${this.filteredPendingImage}&priceToBeDefined=${this.filteredPriceToBeDefined}`)
        this.showToast(
          'Correo enviado',
          'El correo electrónico ha sido enviado correctamente.',
          'success',
        )
      } catch (error) {
        this.showToast('Error de validación', getError(error), 'danger')
      } finally {
        this.isSendingEmail = false
      }
    },

    /**
    * Muestra un mensaje de tipo toast
    * @param {String} title - Título del mensaje
    * @param {String} text - Contenido del mensaje
    * @param {String} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
[class*="collapse-"] {
  .card {
    .card-header {
      cursor: auto;
      padding: 1rem 2.8rem 1rem 1rem;
    }
  }
}
.style-chooser {
  background: #ffffff;
}

.col-padding {
  padding: 0 0.5rem 0 0.5rem;
}
</style>
